import classNames from 'classnames';
import { format as d3Format } from 'd3';
import type { ReactNode } from 'react';

import { InfoTipIcon, RevelioTooltip } from '@revelio/ui';

import type { CommonPlotProps } from '../../types';
import { getFormatter } from '../../utilities';
import { AreaChart, type AreaData } from '../area/area-chart';
import styles from './stat-trend-chart.module.css';

type StatTrendChartProps = {
  title: string;
  info?: ReactNode;
  data: AreaData | null;
} & CommonPlotProps;

export const StatTrendChart = ({
  title,
  info,
  data,
  format,
  renderUpdate,
}: StatTrendChartProps) => {
  //  Hide delta stat because of feedback that it's confusing.
  //  Will rethink how we want to present this
  const HIDE_DELTA = true;

  const values = data?.values ?? [];
  const lastValue = values[values.length - 1]?.value ?? 0;
  const firstValue = values[0]?.value ?? 0;
  const delta = lastValue - firstValue;
  const deltaPercentage = delta / Math.abs(firstValue);

  const formatValue = format
    ? getFormatter(format)
    : (value: number) =>
        Math.abs(value) < 1
          ? Math.round(value).toString()
          : d3Format('.3s')(value);
  const formatDeltaPercentage = d3Format('+.1%');

  const isNegative = deltaPercentage < 0;

  return (
    <div className={styles.container} data-testid="stat-trend-chart">
      <div className={styles.titleContainer}>
        <div className={styles.title}>{title}</div>
        <div className={styles.info}>
          <RevelioTooltip label={info} placement="top">
            <InfoTipIcon size={10} />
          </RevelioTooltip>
        </div>
      </div>
      <div className={styles.chartContainer}>
        <div className={styles.labelContainer}>
          <div className={styles.value} data-testid="stat-trend-chart-value">
            {formatValue(lastValue)}
          </div>
          {!HIDE_DELTA && (
            <div
              className={classNames(styles.delta, {
                // @ts-ignore noUncheckedIndexedAccess
                [styles.positive]: !isNegative,
                // @ts-ignore noUncheckedIndexedAccess
                [styles.negative]: isNegative,
              })}
            >
              {formatDeltaPercentage(deltaPercentage)}
            </div>
          )}
        </div>
        <div className={styles.plotContainer}>
          <AreaChart
            data={data ? [data] : []}
            colors={['#5BD992']}
            showXAxis={false}
            showYAxis={false}
            showGradients={true}
            format={format}
            showNoData={false}
            renderUpdate={renderUpdate}
          />
        </div>
      </div>
    </div>
  );
};
